import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from "@angular/core";

import { TypeManagerDecorator } from "../../../main/type.map.service";
import { ItemTileComponent, tileItemTrackKey, TileItem } from "./item.tile.component";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { SESearchType } from "./se.search.component";
import { S25LoadMoreComponent } from "../../../standalone/s25.load.more.component";
import { IPaginatedData } from "./paginated.list";
import { SeObjectSearchStore } from "./object-search/se.object.search.store";
import { S25CheckboxModule } from "../../s25-checkbox/s25.checkbox.module";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { S25Util } from "../../../util/s25-util";
import { OrganizationProfileApi } from "../../../services/organization/organization.profile.api";
import { AlertService } from "../../../services/alert.service";
import { S25IconButtonComponent } from "../../../standalone/s25.icon.button.component";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { LoginStore } from "../../../stores/login.store";
import { SeSecurityStore } from "../se.security.store";
import { EventSearchExportOptions, SeEventSearchExportService } from "./object-search/se.event.search.export.service";
import { DEFAULT_PAGE_SIZE } from "./se.search.service";
import { S25LoadingInlineStaticComponent } from "../../s25-loading/s25.loading.inline.static.component";
import { OrganizationExportService } from "../export/organization.export.service";

@TypeManagerDecorator("s25-ng-se-search-results")
@Component({
    selector: "s25-ng-se-search-results",
    template: `
        @if (hasSearched()) {
            <div class="results-section" [attr.aria-busy]="displayLoading()">
                @if (!displayResults().length && displayLoading()) {
                    <div class="loading-state" role="status" aria-live="polite">
                        <s25-ng-icon [type]="'refresh'" [size]="24" [ariaLabel]="false" />
                        <p>Searching...</p>
                    </div>
                } @else if (displayResults().length > 0) {
                    <div class="results-header">
                        <h3 id="results-heading">
                            {{ displayCount() }} {{ searchType() === "events" ? "Event" : "Organization"
                            }}{{ displayCount() !== 1 ? "s" : "" }} Found
                        </h3>
                    </div>
                    <div class="action-panel" role="toolbar" aria-label="Results actions">
                        @if (isAdmin()) {
                            <s25-ng-checkbox
                                [value]="allSelected()"
                                [disabled]="selectingAll()"
                                (valueChange)="toggleSelectAll($event)"
                                >{{ allSelected() ? "Deselect All" : "Select All" }}</s25-ng-checkbox
                            >
                            @if (selectingAll()) {
                                <s25-ng-loading-inline-static />
                            }
                            @if (selectedCount()) {
                                <span class="selection-count" aria-live="polite">{{ selectedCount() }} selected</span>
                            }

                            @if (canBulkUpdateOrgStatus()) {
                                <s25-ng-dropdown-search-criteria
                                    [type]="'organizationRatings'"
                                    [placeholder]="'Bulk Update Status'"
                                    [ariaLabel]="'Bulk update status for selected organizations'"
                                    (valueChange)="bulkUpdateRating($event!)"
                                />
                            }

                            @if (searchType() === "organizations") {
                                <s25-ng-button
                                    type="outline"
                                    buttonText="Email Selected"
                                    [disabled]="!selectedIds()?.size"
                                    [ariaLabel]="'Email ' + (selectedIds()?.size || 0) + ' selected organizations'"
                                    (clicked)="emailSelectedOrgs()"
                                />
                                @if (canExportSelectedOrgs()) {
                                    <s25-ng-button
                                        type="outline"
                                        buttonText="Export Selected"
                                        [disabled]="!selectedCount() || exporting()"
                                        [isLoading]="exporting()"
                                        [ariaLabel]="
                                            'Export details for ' + (selectedCount() || 0) + ' selected organizations'
                                        "
                                        (clicked)="onExportSelectedOrgs()"
                                    />
                                }
                            }
                        }
                        <div class="action-panel__trailing">
                            @if (canExportEventResults()) {
                                <s25-ng-button
                                    type="outline"
                                    buttonText="Export Results"
                                    [disabled]="displayLoading() || exporting()"
                                    [isLoading]="exporting()"
                                    (clicked)="onExportEventResults()"
                                />
                            }
                            @if (isSearchView()) {
                                <s25-ng-icon-button
                                    [type]="'refresh'"
                                    [ariaLabel]="'Refresh results'"
                                    (clicked)="refresh.emit()"
                                />
                            }
                        </div>
                    </div>
                    <ul
                        class="results-grid"
                        [class.events-grid]="searchType() === 'events'"
                        [class.orgs-grid]="searchType() === 'organizations'"
                        [attr.aria-label]="resultsGridLabel()"
                        aria-labelledby="results-heading"
                    >
                        @for (item of displayResults(); track trackTileItem(item)) {
                            <li class="results-grid__item">
                                <se-item-tile [item]="item"></se-item-tile>
                            </li>
                        }
                    </ul>

                    <nav aria-label="Load more results">
                        <s25-ng-load-more
                            [currentCount]="displayResults().length"
                            [totalCount]="displayCount()"
                            [isLoading]="displayLoading()"
                            [isLoadingAll]="displayLoadingAll()"
                            (loadMore)="onLoadMore()"
                            (loadAll)="onLoadAll()"
                        ></s25-ng-load-more>
                    </nav>
                } @else {
                    <div class="no-results" role="status">
                        <s25-ng-icon [type]="'search'" [size]="48" aria-hidden="true" [ariaLabel]="false" />
                        <h3>No results found</h3>
                        <p>{{ noResultsMessage() }}</p>
                    </div>
                }
            </div>
        }
    `,
    host: { "[class.admin]": "isAdmin()" },
    styles: `
        .results-section {
            position: relative;
            max-width: 1400px;
            margin: 0 auto;
            background: var(--color--page-bg, #fafaf9);
            border-radius: 8px;
        }

        .loading-state {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 3rem 1rem;
            color: var(--color--text-muted, #666);
        }

        .loading-state p {
            margin-top: 1rem;
            font-size: 1rem;
        }

        ::ng-deep .loading-state svg {
            animation: spin 1s linear infinite;
        }

        @keyframes spin {
            from {
                transform: rotate(0deg);
            }
            to {
                transform: rotate(360deg);
            }
        }

        .results-header {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            margin-bottom: 1.5rem;
        }

        :host.admin .results-header {
            margin: 0;
        }

        .results-header h3 {
            font-size: 1.25rem;
            font-weight: 600;
            color: var(--color--text, #333);
            margin: 0;
        }

        .selection-count {
            font-size: 0.875rem;
            color: var(--color--text-muted, #666);
        }

        s25-ng-dropdown-search-criteria {
            margin-left: auto;
        }

        .action-panel__trailing {
            display: flex;
            align-items: center;
            gap: 1rem;
        }

        :host:not(.admin) .action-panel__trailing {
            margin-left: auto;
        }

        .results-grid {
            display: grid;
            gap: 1.5rem;
            list-style: none;
            padding: 0;
            margin: 0;
        }

        .results-grid__item {
            display: block;
        }

        .results-grid.events-grid {
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        }

        .events-grid .results-grid__item {
            max-width: 500px;
        }

        :host:not(.admin) .results-grid.orgs-grid {
            grid-template-columns: repeat(auto-fill, minmax(480px, 1fr));
        }

        .no-results {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 3rem 1rem;
            text-align: center;
            color: var(--color--text-muted, #666);
        }

        .no-results h3 {
            margin: 1rem 0 0.5rem 0;
            font-size: 1.25rem;
            color: var(--color--text, #333);
        }

        .no-results p {
            margin: 0;
            font-size: 1rem;
        }

        ::ng-deep .no-results svg {
            opacity: 0.4;
        }

        .action-panel {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 1rem;
            margin-bottom: 10px;
        }

        s25-ng-checkbox {
            margin-left: 1rem;
        }

        @media (max-width: 600px) {
            .action-panel {
                gap: 0.5rem;
            }
        }
        :host ::ng-deep .ngDropdownPaginated {
            padding: 2px;
            border: 1px solid var(--color--border-bold, #b3b3b3) !important;
            border-radius: 6px;
        }

        :host ::ng-deep .ngDropdownPaginated .select2-container {
            font-size: 0.875rem;
        }

        :host ::ng-deep .ngDropdownPaginated .select2-container .select2-choice.ui-select-match {
            padding-inline: 5px 1em;
        }

        :host ::ng-deep .ngDropdownPaginated .select2-container .select2-choice.ui-select-match::after {
            right: 4px;
            background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
            width: 17px;
        }

        :host ::ng-deep .ngDropdownPaginated .select2-container .ngDropdownDrop {
            left: 50%;
            transform: translateX(-50%);
            min-width: 11em;
        }

        :host ::ng-deep .ngDropdownPaginated .select2-container .select2-choice.ui-select-match .ngDropdownContent {
            color: var(--color--text);
        }

        @media (max-width: 768px) {
            .results-grid.events-grid {
                grid-template-columns: 1fr;
            }

            :host:not(.admin) .results-grid.orgs-grid {
                grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
            }
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ItemTileComponent,
        S25IconComponent,
        S25LoadMoreComponent,
        S25CheckboxModule,
        S25DropdownPaginatedModule,
        S25IconButtonComponent,
        S25ButtonComponent,
        S25LoadingInlineStaticComponent,
    ],
})
export class SeSearchResultsComponent {
    protected readonly trackTileItem = tileItemTrackKey;

    // Accept either paginated data interface or individual properties for flexibility
    paginatedData = input<IPaginatedData>();

    refresh = output<void>();
    readonly emailSelected = output<number[]>();

    readonly searchStore = inject(SeObjectSearchStore, { optional: true });
    private readonly loginStore = inject(LoginStore);
    private readonly securityStore = inject(SeSecurityStore);

    readonly isAdmin = computed(() => this.searchStore?.isAdmin());
    protected isSearchView = computed(() => !!this.searchStore);
    readonly selectedIds = computed(() => this.searchStore?.selectedIds());
    readonly selectedCount = computed(() => this.selectedIds()?.size ?? 0);
    readonly selectingAll = signal(false);

    private readonly currentItemIds = computed(() => this.displayResults().map((item) => item.itemId));
    readonly allSelected = computed(() => {
        const totalCount = this.displayCount();
        const selectedSize = this.selectedCount();
        if (this.searchType() === "organizations" && selectedSize === totalCount && totalCount > 0) {
            return true;
        }
        const ids = this.currentItemIds();
        const selected = this.selectedIds();
        return ids.length > 0 && ids.every((id) => selected?.has(id));
    });

    // Individual inputs for backward compatibility or non-paginated use cases
    results = input<TileItem[]>([]);
    resultsCount = input<number>(0);
    isSearching = input<boolean>(false);
    currentPage = input<number>(1);

    // Optional configuration with sensible defaults
    hasSearched = input<boolean>(true);
    searchType = input<SESearchType>("events");
    pageSize = input<number>(DEFAULT_PAGE_SIZE);
    noResultsMessage = input<string>("Try adjusting your search terms or filters");
    eventExport = input<EventSearchExportOptions>();

    readonly exporting = signal(false);

    readonly canExportEventResults = computed(
        () =>
            this.searchType() === "events" &&
            !!this.eventExport() &&
            this.loginStore.loggedIn() &&
            this.securityStore.flsStore.hasRead("REPORT_SECURITY"),
    );

    readonly canExportSelectedOrgs = computed(
        () => this.searchType() === "organizations" && this.isAdmin() && this.securityStore.hasFLSOverride(),
    );

    readonly canBulkUpdateOrgStatus = computed(
        () => this.searchType() === "organizations" && this.isAdmin() && this.securityStore.canEditOrgRating(),
    );

    // Output events
    pageChange = output<number>();
    loadMore = output<void>();

    // Computed signals that use paginated data if available, otherwise fall back to inputs
    displayResults = computed(() => this.paginatedData()?.itemsForDisplay() ?? this.results());
    displayCount = computed(() => this.paginatedData()?.totalCount() ?? this.resultsCount());
    displayLoading = computed(() => this.paginatedData()?.loading() ?? this.isSearching());
    displayLoadingAll = computed(() => this.paginatedData()?.loadingAll() ?? false);
    displayPage = computed(() => this.paginatedData()?.page() ?? this.currentPage());

    protected readonly resultsGridLabel = computed(() => {
        const type = this.searchType() === "events" ? "events" : "organizations";
        return `${this.displayCount()} ${type} found`;
    });

    /**
     * Handle page change from pagination component
     */
    onPageChange(newPage: number): void {
        this.pageChange.emit(newPage);
    }

    /**
     * Handle load more from load more component
     */
    async onLoadMore(): Promise<void> {
        const paginated = this.paginatedData();
        if (paginated?.loadMore) {
            await paginated.loadMore();
        } else {
            this.loadMore.emit();
        }
    }

    /**
     * Handle load all from load more component
     */
    async onLoadAll(): Promise<void> {
        const paginated = this.paginatedData();
        if (paginated?.loadAll) {
            await paginated.loadAll();
        }
    }

    async toggleSelectAll(selectAll: boolean): Promise<void> {
        if (!this.searchStore) return;
        if (!selectAll) {
            this.searchStore.deSelectAll();
            return;
        }

        const paginated = this.paginatedData();
        if (this.searchType() === "organizations" && paginated?.hasMore()) {
            this.selectingAll.set(true);
            try {
                await paginated.loadAll();
                const allIds = this.displayResults().map((item) => item.itemId);
                this.searchStore.selectAll(allIds);
            } finally {
                this.selectingAll.set(false);
            }
        } else {
            this.searchStore.selectAll(this.currentItemIds());
        }
    }

    async bulkUpdateRating(rating: DropDownItem) {
        if (!rating || !this.searchStore?.selectedIds().size) return;
        const orgIds = [...this.searchStore.selectedIds()];
        const [result, error] = await S25Util.Maybe(OrganizationProfileApi.saveRating(orgIds, Number(rating.itemId)));
        if (error) {
            S25Util.showError(error);
            return;
        }
        if (result?.skipped?.length) {
            const skippedSummary = result.skipped.map((entry) => `${entry.orgId} (${entry.reason})`).join(", ");
            AlertService.alert(`Updated ${result.updatedOrgIds.length} organization(s). Skipped: ${skippedSummary}`);
        }
        this.refresh.emit();
    }

    emailSelectedOrgs() {
        const ids = [...(this.searchStore?.selectedIds() ?? [])];
        if (ids.length) this.emailSelected.emit(ids);
    }

    async onExportSelectedOrgs(): Promise<void> {
        const organizationIds = [...(this.searchStore?.selectedIds() ?? [])];
        if (!organizationIds.length || this.exporting()) {
            return;
        }
        this.exporting.set(true);
        try {
            await OrganizationExportService.openAndExport({
                mode: "bulkByOrgIds",
                organizationIds,
            });
        } finally {
            this.exporting.set(false);
        }
    }

    async onExportEventResults(): Promise<void> {
        const exportOptions = this.eventExport();
        if (!exportOptions || this.exporting()) {
            return;
        }
        this.exporting.set(true);
        try {
            await SeEventSearchExportService.exportEventSearchResults({
                ...exportOptions,
                isLoading: this.displayLoading(),
                isLoggedIn: this.loginStore.loggedIn(),
                resultCount: this.displayCount(),
            });
        } catch (caughtError) {
            S25Util.showError(caughtError);
        } finally {
            this.exporting.set(false);
        }
    }
}
